import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Sidebar } from '../../components/ui/sidebar';
import { MessageSquare } from 'lucide-react';
import { useAgents } from '../../contexts/AgentContext';
import { usePersonas } from '../../contexts/PersonaContext';
import { useSimulations } from '../../contexts/SimulationContext';
import { getPersonaResponse } from '../../lib/api';
import { isConnectedAgent } from '../../lib/edge-functions';
import {
  FunctionError,
  getAgentReply,
  raiseSimulationSpendLimit,
} from '../../lib/managed-agents/api';
import { useSimulationStore } from '../../stores/simulationStore';
import { Message } from '../../lib/types';
import { useAuthStore } from '../../stores/authStore';
import { AbortError, QuotaExceededError } from '../../lib/custom-errors';
import { PageHeader } from '../../components/ui/page-header/page-header';
import { SimulationSettings } from '../../components/ui/conversation-simulation/simulation-settings';
import { PastSimulations } from '../../components/ui/conversation-simulation/past-simulations';
import { Conversation } from '../../components/ui/conversation-simulation/conversation';
import { getCurrentTurns } from '../../lib/utils/conversation-simulation/get-current-turns';
import { describeEarlyStop } from '../../lib/utils/simulation-status';
import { useTutorialStore } from '../../stores/tutorialStore';

const MAX_TURNS = 10;

export const ConversationSimulation = (): JSX.Element => {
  const { simulationId: simulationIdFromUrl } = useParams<{
    simulationId?: string;
  }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const {
    isActive: isOnboardingActive,
    markMilestoneComplete,
    setOnboardingSimulation,
  } = useTutorialStore();
  const { agents } = useAgents();
  const { personas } = usePersonas();
  const {
    simulations,
    loading,
    createSimulation,
    updateSimulation,
    fetchSimulationMessages,
  } = useSimulations();

  const {
    isSimulating,
    selectedPersonas,
    selectedPersonaTab,
    selectedSimulation,
    setSimulationState,
    resetSimulation,
  } = useSimulationStore();

  const location = useLocation();
  // "Run a simulation" on Connections passes the agent to start with.
  const [selectedAgent, setSelectedAgent] = useState<string | null>(
    () => (location.state as { agentId?: string } | null)?.agentId ?? null,
  );
  const [userMessage, setUserMessage] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  // Three turns during the guided tour keeps the first wait short.
  const defaultTurns = isOnboardingActive ? '3' : '5';
  const [conversationTurns, setConversationTurns] = useState(defaultTurns);
  const stopSimulationRef = useRef<AbortController | null>(null);
  const [isStopping, setIsStopping] = useState(false);
  const [typingRole, setTypingRole] = useState<
    Record<string, 'user' | 'assistant' | null>
  >({});

  useEffect(() => {
    if (loading || isSimulating) return;
    if (!simulationIdFromUrl) {
      if (isOnboardingActive && selectedSimulation) {
        setSelectedAgent(null);
        resetSimulation();
        setConversationTurns(defaultTurns);
        return;
      }
      if (selectedSimulation && selectedPersonas.length <= 1) resetSimulation();
      return;
    }
    const simulation = simulations.find((s) => s.id === simulationIdFromUrl);
    if (!simulation) {
      navigate('/conversation-simulation', { replace: true });
      resetSimulation();
      return;
    }
    if (selectedSimulation === simulationIdFromUrl) return;
    setSimulationState({
      selectedSimulation: simulationIdFromUrl,
      selectedPersonaTab: simulation.persona_id,
      personaSimulations: {
        [simulation.persona_id]: simulationIdFromUrl,
      },
      personaMessages: {
        [simulation.persona_id]: simulation.messages ?? [],
      },
      selectedPersonas: [simulation.persona_id],
      isSimulating: false,
      simulationProgress: null,
    });
  }, [
    simulationIdFromUrl,
    simulations,
    loading,
    isOnboardingActive,
    defaultTurns,
    isSimulating,
    selectedSimulation,
    selectedPersonas.length,
    navigate,
    resetSimulation,
    setSimulationState,
  ]);

  useEffect(() => {
    if (selectedSimulation) {
      const simulation = simulations.find((s) => s.id === selectedSimulation);
      if (simulation) {
        setSelectedAgent(simulation.agent_id);
        if (selectedPersonas.length === 0) {
          const { personaSimulations } = useSimulationStore.getState();
          setSimulationState({
            selectedPersonas: [simulation.persona_id],
            selectedPersonaTab: simulation.persona_id,
            personaMessages: {
              [simulation.persona_id]: simulation.messages,
            },
            personaSimulations: {
              ...personaSimulations,
              [simulation.persona_id]: selectedSimulation,
            },
          });
        }

        // Default to the first valid continuation turn (one past what's already
        // run) so "Continue" works immediately, without overwriting a higher
        // value the user already chose.
        const currentTurns = Math.ceil(simulation.messages.length / 2);
        const minSelectableTurns = Math.min(currentTurns + 1, MAX_TURNS);
        if (parseInt(conversationTurns) < minSelectableTurns) {
          setConversationTurns(minSelectableTurns.toString());
        }
      }
    }
  }, [
    selectedSimulation,
    simulations,
    selectedPersonas,
    setSimulationState,
    conversationTurns,
  ]);

  /** The agent still owes a reply: the last message is the persona's. */
  const isAwaitingAgentReply = () => {
    if (!selectedPersonaTab) return false;
    const { personaMessages } = useSimulationStore.getState();
    const messages = personaMessages[selectedPersonaTab] || [];
    return messages[messages.length - 1]?.role === 'user';
  };

  const isMaxTurnsReached = () => {
    if (!selectedPersonaTab) return false;
    // A run that stopped before the agent's last reply can always finish it.
    if (isAwaitingAgentReply()) return false;
    const { personaMessages } = useSimulationStore.getState();
    const messages = personaMessages[selectedPersonaTab] || [];
    return getCurrentTurns(messages) >= MAX_TURNS;
  };

  const handleSendMessage = async () => {
    if (
      !userMessage.trim() ||
      !selectedAgent ||
      !selectedPersonaTab ||
      isSendingMessage
    )
      return;

    setIsSendingMessage(true);
    let finalized = false;
    let generationLockedAt: string | undefined;
    // A simulation this send created for a connected agent.
    let startedSimulation: { id: string; generationId: string } | undefined;
    let sendError: unknown;
    // A manual message doesn't finish a simulation that stopped early.
    const settledStatus =
      simulations.find((s) => s.id === selectedSimulation)?.status === 'failed'
        ? 'failed'
        : 'complete';
    try {
      if (selectedSimulation) {
        const locked = await updateSimulation(selectedSimulation, {
          status: 'generating',
          generation_id: crypto.randomUUID(),
        });
        generationLockedAt = locked?.updated_at;
      }
      const { personaMessages } = useSimulationStore.getState();

      const simulationMessages = selectedSimulation
        ? await fetchSimulationMessages(selectedSimulation)
        : [];

      const updatedMessagesState = [
        ...simulationMessages,
        { role: 'user', content: userMessage } as Message,
      ];
      setSimulationState({
        personaMessages: {
          ...personaMessages,
          [selectedPersonaTab]: updatedMessagesState,
        },
      });
      setUserMessage('');

      const agent = agents.find((a) => a.id === selectedAgent);
      let simulationId = selectedSimulation;
      // Connected agents keep one session per simulation, so the simulation
      // has to exist before their first reply.
      // It's saved with the message, so a failed reply leaves something to
      // continue rather than an empty conversation.
      if (!simulationId && isConnectedAgent(agent)) {
        const generationId = crypto.randomUUID();
        const created = await createSimulation({
          name: `${agent?.name} + ${personas.find((p) => p.id === selectedPersonaTab)?.name}`,
          agent_id: selectedAgent,
          persona_id: selectedPersonaTab,
          messages: updatedMessagesState,
          status: 'generating',
          generation_id: generationId,
        });
        simulationId = created.id;
        startedSimulation = { id: created.id, generationId };
        const { personaSimulations } = useSimulationStore.getState();
        setSimulationState({
          personaSimulations: {
            ...personaSimulations,
            [selectedPersonaTab]: created.id,
          },
          selectedSimulation: created.id,
        });
        navigate(`/conversation-simulation/${created.id}`, { replace: true });
      }

      const response = await getAgentReply({
        agent,
        message: userMessage,
        history: simulationMessages,
        workspaceId: user?.currentWorkspace?.workspace_id,
        simulationId,
      });

      const responseContent =
        typeof response === 'object' &&
        response !== null &&
        'content' in response
          ? response.content
          : response;

      const assistantMessage: Message = {
        role: 'assistant',
        content: responseContent,
        ...(typeof response === 'object' &&
        response !== null &&
        'metadata' in response
          ? { metadata: response.metadata }
          : {}),
      };

      const updatedMessages = [...updatedMessagesState, assistantMessage];
      setSimulationState({
        personaMessages: {
          ...personaMessages,
          [selectedPersonaTab]: updatedMessages,
        },
      });

      if (simulationId) {
        await updateSimulation(simulationId, {
          messages: updatedMessages,
          status: settledStatus,
        });
        finalized = true;
      } else {
        const simulation = await createSimulation({
          name: `${agents.find((a) => a.id === selectedAgent)?.name} + ${personas.find((p) => p.id === selectedPersonaTab)?.name}`,
          agent_id: selectedAgent,
          persona_id: selectedPersonaTab,
          messages: updatedMessages,
        });
        const { personaSimulations } = useSimulationStore.getState();
        setSimulationState({
          personaSimulations: {
            ...personaSimulations,
            [selectedPersonaTab]: simulation.id,
          },
          selectedSimulation: simulation.id,
        });
        navigate(`/conversation-simulation/${simulation.id}`, {
          replace: true,
        });
      }
    } catch (error) {
      sendError = error;
      console.error('Error sending message:', error);
      toast.error(
        error instanceof Error ? error.message : 'Failed to send message',
      );
    } finally {
      // A new conversation whose first reply failed: keep the message and
      // mark it stopped, so it can be continued instead of looking finished.
      if (startedSimulation && !finalized) {
        await updateSimulation(
          startedSimulation.id,
          {
            status: 'failed',
            failure_reason: describeEarlyStop(1, 1, sendError),
            failure_code:
              sendError instanceof FunctionError
                ? (sendError.code ?? null)
                : null,
          },
          // Only if this send still owns it: another tab continuing the
          // conversation takes a new generation ID. Not updated_at, which the
          // agent's own turn changes (a Claude Console turn records the agent
          // version).
          { requireGenerationId: startedSimulation.generationId },
        ).catch((err) =>
          console.error('Failed to mark the simulation stopped:', err),
        );
      }
      // If the response failed before we saved, don't leave the row locked as
      // 'generating' — but release the lock only if the row is unchanged since
      // we claimed it. If another tab/run has taken over generation (bumping
      // updated_at), leave its state intact rather than marking a live
      // simulation complete and letting it be evaluated mid-flight.
      if (selectedSimulation && !finalized && generationLockedAt) {
        await updateSimulation(
          selectedSimulation,
          { status: settledStatus },
          { requireUpdatedAt: generationLockedAt },
        ).catch((err) =>
          console.error('Failed to reset simulation status:', err),
        );
      }
      setIsSendingMessage(false);
    }
  };

  const newSimulation = () => {
    setSelectedAgent(null);
    resetSimulation();
    setConversationTurns(defaultTurns);
    navigate('/conversation-simulation', { replace: true });
  };

  const handleSimulationSelect = (simulationId: string | null) => {
    if (simulationId) {
      navigate(`/conversation-simulation/${simulationId}`, { replace: true });
    } else {
      navigate('/conversation-simulation', { replace: true });
    }
  };

  const stopSimulation = () => {
    if (stopSimulationRef.current) {
      stopSimulationRef.current.abort();
      stopSimulationRef.current = null;
    }
    setIsStopping(true);
  };

  /** `resume` finishes a stopped simulation without asking for more turns. */
  const startSimulation = async (options?: { resume?: boolean }) => {
    if (!selectedAgent || selectedPersonas.length === 0) {
      toast.warning(
        'Please select an agent and at least one persona before starting the simulation',
      );
      return;
    }

    if (isMaxTurnsReached()) {
      toast.warning('Maximum number of turns reached');
      return;
    }

    if (
      !options?.resume &&
      selectedSimulation &&
      selectedPersonaTab &&
      !isAwaitingAgentReply()
    ) {
      const { personaMessages } = useSimulationStore.getState();
      const currentTurns = getCurrentTurns(
        personaMessages[selectedPersonaTab] || [],
      );
      if (parseInt(conversationTurns) <= currentTurns) {
        toast.warning('Please increase the number of turns first');
        return;
      }
    }

    const controller = new AbortController();
    stopSimulationRef.current = controller;
    const { signal } = controller;
    // Whether this is the guide's own run, decided now: quitting the guide
    // while it runs must not stop its conversation from being remembered.
    const tutorialAtStart = useTutorialStore.getState();
    const isGuidedRun =
      tutorialAtStart.isActive &&
      !tutorialAtStart.completedMilestones.simulationRun;

    setIsStopping(false);
    setSimulationState({
      isSimulating: true,
      selectedPersonaTab: selectedPersonas?.[0],
    });
    const turns = parseInt(conversationTurns);
    const totalPersonas = selectedPersonas.length;
    const newPersonaMessages: Record<string, Message[]> = {};
    let didPersistSimulation = false;
    const touchedSimulationIds = new Set<string>();
    // Simulations that stopped before reaching their turns, with the reason.
    // They are saved as failed, so a half conversation is never scored.
    const earlyStops = new Map<string, { reason: string; code?: string }>();
    const earlyStop = (turn: number, error: unknown) => ({
      reason: describeEarlyStop(turn, turns, error),
      code: error instanceof FunctionError ? error.code : undefined,
    });

    try {
      for (
        let personaIndex = 0;
        personaIndex < selectedPersonas.length;
        personaIndex++
      ) {
        const { personaMessages } = useSimulationStore.getState();
        const personaId = selectedPersonas[personaIndex];
        const persona = personas.find((p) => p.id === personaId);
        const agent = agents.find((a) => a.id === selectedAgent);
        let existingMessages = personaMessages[personaId] || [];

        // New simulation: start with the agent's opening message if it has one.
        // Without one, the persona speaks first.
        if (!existingMessages?.length) {
          const openingMessage = agent?.greeting?.trim();
          existingMessages = openingMessage
            ? [{ role: 'assistant', content: openingMessage } as Message]
            : [];

          // Show the opening message immediately before waiting for the persona
          if (existingMessages.length) {
            setSimulationState({
              personaMessages: {
                ...useSimulationStore.getState().personaMessages,
                [personaId]: [...existingMessages],
              },
            });
          }

          setTypingRole((prev) => ({ ...prev, [personaId]: 'user' }));
          const personaResponse = await getPersonaResponse(
            personaId,
            existingMessages,
            user?.currentWorkspace?.workspace_id,
            signal,
          );
          existingMessages.push({ role: 'user', content: personaResponse });
        }

        setSimulationState({
          simulationProgress: {
            persona: persona?.name || 'Unknown',
            current: personaIndex + 1,
            total: totalPersonas,
            turn:
              existingMessages.length > 0
                ? Math.floor(existingMessages.length / 2)
                : 0,
            totalTurns: turns,
          },
        });

        let simulationId: string | undefined;
        let messages = existingMessages;
        try {
          newPersonaMessages[personaId] = messages;
          setSimulationState({
            personaMessages: {
              ...personaMessages,
              [personaId]: messages,
            },
          });

          let simulation;
          if (selectedSimulation && personaId === selectedPersonaTab) {
            simulation = { id: selectedSimulation };
            await updateSimulation(selectedSimulation, {
              messages,
              status: 'generating',
              generation_id: crypto.randomUUID(),
            });
            didPersistSimulation = true;
          } else {
            simulation = await createSimulation({
              name: `${agents.find((a) => a.id === selectedAgent)?.name} + ${persona?.name}`,
              agent_id: selectedAgent,
              persona_id: personaId,
              messages: messages,
              status: 'generating',
              generation_id: crypto.randomUUID(),
            });
            didPersistSimulation = true;
          }
          touchedSimulationIds.add(simulation.id);
          simulationId = simulation.id;

          const { personaSimulations, selectedPersonaTab: visiblePersonaTab } =
            useSimulationStore.getState();
          setSimulationState({
            personaSimulations: {
              ...personaSimulations,
              [personaId]: simulation.id,
            },
            // Keep selectedSimulation in sync with the visible tab so manual send uses the right simulation
            ...(personaId === visiblePersonaTab
              ? { selectedSimulation: simulation.id }
              : {}),
          });
          if (personaId === visiblePersonaTab) {
            navigate(`/conversation-simulation/${simulation.id}`, {
              replace: true,
            });
          }

          // Pre-loop: if the last message is from the persona, close the half-turn
          // with a bot response so every loop iteration is always persona → bot.
          const lastMessage = messages[messages.length - 1];
          if (lastMessage?.role === 'user') {
            setTypingRole((prev) => ({ ...prev, [personaId]: 'assistant' }));
            const closingBotResponse = await getAgentReply({
              agent,
              message: lastMessage.content,
              history: messages.slice(0, -1),
              workspaceId: user?.currentWorkspace?.workspace_id,
              simulationId: simulation.id,
              signal,
            });
            messages.push({
              role: 'assistant',
              content:
                typeof closingBotResponse === 'object'
                  ? closingBotResponse.content
                  : closingBotResponse,
              ...(typeof closingBotResponse === 'object' &&
              closingBotResponse?.metadata
                ? { metadata: closingBotResponse.metadata }
                : {}),
            } as Message);
            newPersonaMessages[personaId] = messages;
            setSimulationState({
              personaMessages: {
                ...useSimulationStore.getState().personaMessages,
                [personaId]: [...messages],
              },
            });
            await updateSimulation(simulation.id, { messages });
          }

          // Messages now always end with 'assistant'; every turn is persona → bot.
          const currentTurns = Math.floor(messages.length / 2);
          const remainingTurns = turns - currentTurns;

          setTypingRole((prev) => ({ ...prev, [personaId]: null }));
          for (let i = 0; i < remainingTurns; i++) {
            const { personaMessages } = useSimulationStore.getState();

            setSimulationState({
              simulationProgress: {
                persona: persona?.name || 'Unknown',
                current: personaIndex + 1,
                total: totalPersonas,
                turn: currentTurns + i + 1,
                totalTurns: turns,
              },
            });

            try {
              setTypingRole((prev) => ({ ...prev, [personaId]: 'user' }));
              const personaResponse = await getPersonaResponse(
                personaId,
                messages,
                user?.currentWorkspace?.workspace_id,
                signal,
              );
              messages.push({ role: 'user', content: personaResponse });
              // Show persona message immediately while bot is fetching its response
              setTypingRole((prev) => ({ ...prev, [personaId]: 'assistant' }));
              setSimulationState({
                personaMessages: {
                  ...personaMessages,
                  [personaId]: [...messages],
                },
              });
              // Saved before the agent replies: if the reply fails, continuing
              // resends this same message instead of writing a new one.
              await updateSimulation(simulation.id, { messages });

              const nextAgentResponse = await getAgentReply({
                agent,
                message: personaResponse,
                history: messages.slice(0, -1),
                workspaceId: user?.currentWorkspace?.workspace_id,
                simulationId: simulation.id,
                signal,
              });
              messages.push({
                role: 'assistant',
                content:
                  typeof nextAgentResponse === 'object'
                    ? nextAgentResponse.content
                    : nextAgentResponse,
                ...(typeof nextAgentResponse === 'object' &&
                nextAgentResponse?.metadata
                  ? { metadata: nextAgentResponse.metadata }
                  : {}),
              } as Message);

              newPersonaMessages[personaId] = messages;
              setSimulationState({
                personaMessages: {
                  ...personaMessages,
                  [personaId]: [...messages],
                },
              });

              await updateSimulation(simulation.id, { messages });
            } catch (error) {
              if (error instanceof AbortError) break;
              earlyStops.set(
                simulation.id,
                earlyStop(currentTurns + i + 1, error),
              );
              if (error instanceof QuotaExceededError) throw error;

              console.error(`Error in conversation turn ${i}:`, error);
              toast.error(
                error instanceof Error
                  ? error.message
                  : 'Failed to perform conversation turn',
              );
              break;
            }
          }
        } catch (error) {
          if (
            simulationId &&
            !earlyStops.has(simulationId) &&
            !(error instanceof AbortError)
          ) {
            earlyStops.set(
              simulationId,
              earlyStop(Math.floor((messages.length - 1) / 2) + 1, error),
            );
          }
          if (error instanceof QuotaExceededError) throw error;
          console.error(`Error with persona ${personaId}:`, error);
          continue;
        } finally {
          setTypingRole((prev) => ({ ...prev, [personaId]: null }));
        }
      }
    } catch (error: any) {
      if (error instanceof AbortError) return;

      console.error('Error running simulation:', error);
      toast.error(
        error instanceof Error ? error.message : 'Failed to run simulation',
      );
    } finally {
      await Promise.all(
        [...touchedSimulationIds].map((id) => {
          const stop = earlyStops.get(id);
          return updateSimulation(
            id,
            stop
              ? {
                  status: 'failed',
                  failure_reason: stop.reason,
                  failure_code: stop.code ?? null,
                }
              : {
                  status: 'complete',
                  failure_reason: null,
                  failure_code: null,
                },
          ).catch((err) =>
            console.error('Failed to finalize simulation status:', err),
          );
        }),
      );

      // Only a run that finished moves the guide on: a failed one can't be
      // evaluated, so the guide would wait for a button that never shows.
      if (didPersistSimulation && earlyStops.size === 0) {
        // Remember the guide's own run, so resuming the guide reopens it. Only
        // that one: later runs, or runs started while the guide is paused,
        // must not replace it.
        if (isGuidedRun) {
          setOnboardingSimulation(
            useSimulationStore.getState().selectedSimulation ??
              [...touchedSimulationIds][0] ??
              null,
          );
        }
        markMilestoneComplete('simulationRun');
      }
      setSimulationState({
        isSimulating: false,
        simulationProgress: null,
      });
      setTypingRole({});
      setIsStopping(false);
      stopSimulationRef.current = null;
    }
  };

  const failures = Object.fromEntries(
    simulations
      .filter((s) => s.status === 'failed')
      .map((s) => [
        s.id,
        {
          reason: s.failure_reason ?? 'This simulation stopped early.',
          code: s.failure_code ?? null,
        },
      ]),
  );

  const raiseLimitAndContinue = async (
    simulationId: string,
    budgetCents: number,
  ) => {
    const workspaceId = user?.currentWorkspace?.workspace_id;
    if (!workspaceId) return;
    try {
      await raiseSimulationSpendLimit(workspaceId, simulationId, budgetCents);
      toast.success('Spend limit raised. Continuing the simulation.');
      await startSimulation({ resume: true });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Try again.');
    }
  };

  const canStartConversation =
    selectedAgent &&
    selectedPersonas.length > 0 &&
    !isSimulating &&
    !isMaxTurnsReached();

  return (
    <div className="flex h-screen bg-prompt-safecomwhite">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <div className="flex h-full w-full flex-col">
          <PageHeader
            title="Conversation Simulation"
            icon={
              <MessageSquare className="h-6 w-6 text-prompt-safecomoutrageous-orange" />
            }
          />

          <div className="flex gap-6 p-6 max-lg:flex-col">
            <div className="space-y-6 lg:w-80">
              <SimulationSettings
                selectedAgent={selectedAgent}
                setSelectedAgent={setSelectedAgent}
                conversationTurns={conversationTurns}
                setConversationTurns={setConversationTurns}
                isStopping={isStopping}
                startSimulation={startSimulation}
                stopSimulation={stopSimulation}
                newSimulation={newSimulation}
                isMaxTurnsReached={isMaxTurnsReached}
                maxTurns={MAX_TURNS}
              />
              <PastSimulations
                setSelectedAgent={setSelectedAgent}
                isSimulating={isSimulating}
                onSimulationSelect={handleSimulationSelect}
              />
            </div>

            <div className="flex-1">
              <Conversation
                canStartConversation={!!canStartConversation}
                userMessage={userMessage}
                setUserMessage={setUserMessage}
                handleSendMessage={handleSendMessage}
                isSendingMessage={isSendingMessage}
                typingRole={typingRole}
                failures={failures}
                onRaiseLimitAndContinue={raiseLimitAndContinue}
                isImportedAgent={isConnectedAgent(
                  agents.find((a) => a.id === selectedAgent),
                )}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
